/**
 * Snippets come from the API as text with <mark> tags (ts_headline does not escape the text
 * itself). Escape everything, then restore only the mark tags, so document text can never
 * inject HTML.
 */
export function snippetToHtml(snippet: string): string {
  const escaped = snippet
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return escaped.replace(/&lt;mark&gt;/g, "<mark>").replace(/&lt;\/mark&gt;/g, "</mark>");
}

/**
 * Highlight terms as single lower-case words. In exact mode the API sends whole phrases; the text
 * layer and the OCR boxes compare word by word, so a phrase is split into its words here.
 */
export function highlightWords(terms: readonly string[]): string[] {
  const words = terms.flatMap((t) => t.toLowerCase().split(/[^\p{L}\p{N}]+/u));
  return [...new Set(words.filter((w) => w.length > 1))];
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Wraps the highlight terms of the query in <mark> inside escaped plain text. */
export function highlightPlain(text: string, terms: readonly string[]): string {
  const escaped = escapeHtml(text);
  const lower = highlightWords(terms);
  if (lower.length === 0) return escaped;
  return escaped.replace(/[\p{L}\p{N}]+/gu, (w) =>
    lower.includes(w.toLowerCase()) ? `<mark>${w}</mark>` : w,
  );
}

/**
 * highlightPlain for text whose readable form differs character by character (a legacy-font
 * text layer and its ArmSCII-8 decoding): words are matched in `readable`, the marks wrap the
 * same positions of `text`, which is what stays on the page.
 */
export function highlightMapped(text: string, readable: string, terms: readonly string[]): string {
  const lower = highlightWords(terms);
  if (lower.length === 0 || readable.length !== text.length) return escapeHtml(text);
  let out = "";
  let last = 0;
  for (const m of readable.matchAll(/[\p{L}\p{N}]+/gu)) {
    if (!lower.includes(m[0].toLowerCase())) continue;
    const end = m.index + m[0].length;
    out += `${escapeHtml(text.slice(last, m.index))}<mark>${escapeHtml(text.slice(m.index, end))}</mark>`;
    last = end;
  }
  return out + escapeHtml(text.slice(last));
}
